import dayjs, { type Dayjs } from 'dayjs';
import { RRule, RRuleSet, Frequency, rrulestr, datetime, type Weekday, type WeekdayStr } from 'rrule';
import type { Options, ParsedOptions } from 'rrule/dist/esm/types';
import type { ScheduleUpsertModalB50FormValues } from '../types/MachineDetailsRobotSchedule.types';
import { DateTime } from 'lib/utils/date-handling/DateTime';

const RRULE_BY_WEEKDAY: Record<WeekdayStr, Weekday> = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
};

/** dayjs `.day()` index → WeekdayStr (0 = Sunday). */
const WEEKDAY_STR_BY_DAYJS_DAY: WeekdayStr[] = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

const DAYJS_DAY_BY_WEEKDAY_STR: Record<WeekdayStr, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

/** Map form `WeekdayStr` values to rrule `Weekday` instances (e.g. `RRule.MO`). */
export const toRruleWeekdays = (weekdays: WeekdayStr[]): Weekday[] => weekdays.map(day => RRULE_BY_WEEKDAY[day]);

/**
 * Build an RRuleSet from options or an RRULE string. `new RRule` / `RRule.fromString` only
 * understand RRULE/EXRULE/DTSTART and throw on any other component (e.g. `Unsupported RFC prop
 * EXDATE`). `rrulestr(..., { forceset: true })` returns an RRuleSet that accepts those extra lines.
 */
export function toRRuleSet(rruleStringOrOptions: string | Partial<Options>): RRuleSet {
  const rruleString =
    typeof rruleStringOrOptions === 'string' ? rruleStringOrOptions : RRule.optionsToString(rruleStringOrOptions);

  return rrulestr(rruleString, { forceset: true }) as RRuleSet;
}

/**
 * Parse an RRULE string into its options, tolerating EXDATE/RDATE lines.
 */
export const parseRRuleString = (rruleString: string): ParsedOptions => toRRuleSet(rruleString).rrules()[0].options;

/**
 * Map local form weekdays to rrule weekdays in UTC for the given local datetime.
 * Needed when DTSTART is stored as UTC (`Z`) so BYDAY matches the UTC calendar day.
 */
export const toRruleWeekdaysUtc = (localWeekdays: WeekdayStr[], localDateTime: Dayjs): Weekday[] => {
  const dayShift = localDateTime.utc().day() - localDateTime.day();

  return localWeekdays.map(day => {
    const shiftedDayjsDay = (DAYJS_DAY_BY_WEEKDAY_STR[day] + dayShift + 7) % 7;

    return RRULE_BY_WEEKDAY[WEEKDAY_STR_BY_DAYJS_DAY[shiftedDayjsDay]];
  });
};

/**
 * Inverse of `toRruleWeekdaysUtc`: map UTC BYDAY weekdays back to local form weekdays
 * for the given local datetime (e.g. when hydrating the edit form from an rrule).
 */
export const fromRruleWeekdaysUtc = (utcWeekdays: WeekdayStr[], localDateTime: Dayjs): WeekdayStr[] => {
  const dayShift = localDateTime.utc().day() - localDateTime.day();

  return utcWeekdays.map(day => {
    const shiftedDayjsDay = (DAYJS_DAY_BY_WEEKDAY_STR[day] - dayShift + 7) % 7;

    return WEEKDAY_STR_BY_DAYJS_DAY[shiftedDayjsDay];
  });
};

export const toOneTimeRRuleSet = ({ dtstart }: { dtstart: Date }): RRuleSet =>
  toRRuleSet({
    freq: Frequency.DAILY,
    interval: 1,
    count: 1,
    dtstart,
    tzid: DateTime.getBrowserTimeZone(),
  });

/**
 * Move DTSTART of `sourceRRule` to the real instant `occurrenceStart` (expressed in the rule's own TZID),
 * keeping every RRULE option and the EXDATE/RDATE on or after it.
 */
export const rebaseRRuleStart = ({
  sourceRRule,
  occurrenceStart,
}: {
  sourceRRule: string;
  occurrenceStart: string | Date;
}): RRuleSet => {
  const source = toRRuleSet(sourceRRule);
  const { tzid, ...options } = source.rrules()[0].origOptions;
  const local = dayjs(occurrenceStart).tz(tzid ?? 'UTC');
  const dtstart = datetime(local.year(), local.month() + 1, local.date(), local.hour(), local.minute(), local.second());
  const target = new RRuleSet();

  target.rrule(new RRule({ ...options, tzid, dtstart }));
  source.exdates().forEach(exdate => exdate >= dtstart && target.exdate(exdate));
  source.rdates().forEach(rdate => rdate >= dtstart && target.rdate(rdate));

  return target;
};

export const toCV50UntilDate = ({ until }: { until: Date }): Date =>
  dayjs.utc(until).hour(23).minute(59).second(0).millisecond(0).toDate();

/** Repeating rule only when the toggle is on; otherwise a one-time rule, whatever the other repeat fields hold. */
export const buildScheduleRRuleSet = ({
  values,
  isRepeating,
}: {
  values: ScheduleUpsertModalB50FormValues;
  isRepeating: boolean;
}): RRuleSet => {
  const occurrenceStart = values.scheduleDate
    .hour(values.scheduleTime.hour())
    .minute(values.scheduleTime.minute())
    .second(0)
    .millisecond(0);

  const untilDate = values.scheduleEndDate
    ? values.scheduleEndDate
        .hour(values.scheduleTime.hour())
        .minute(values.scheduleTime.minute())
        .second(0)
        .millisecond(0)
    : undefined;

  const dtstart = datetime(
    occurrenceStart.year(),
    occurrenceStart.month() + 1, // rrule uses 1-12 for months
    occurrenceStart.date(),
    occurrenceStart.hour(),
    occurrenceStart.minute()
  );

  if (!isRepeating) {
    return toOneTimeRRuleSet({ dtstart });
  }

  const until = untilDate
    ? datetime(untilDate.year(), untilDate.month() + 1, untilDate.date(), untilDate.hour(), untilDate.minute())
    : undefined;
  const scheduleFrequency = values.scheduleFrequency ?? Frequency.WEEKLY;

  return toRRuleSet({
    freq: scheduleFrequency,
    interval: Math.min(Math.max(values.scheduleEvery ?? 1, 1), 5),
    dtstart,
    until,
    byweekday:
      scheduleFrequency === Frequency.WEEKLY && values.weekdays?.length ? toRruleWeekdays(values.weekdays) : undefined,
    tzid: DateTime.getBrowserTimeZone(),
  });
};
